import { AddressingMode } from "../../interfaces/op-code";
import { Cpu } from "./cpu";
import { Logger } from "../../util/logger";
import { padHex } from "../../util/helpers";

export function getAddressingMode(cpu: Cpu, mode: AddressingMode): () => void {
    const logger = new Logger('ADDR', 0);
    const addressingMap: { [key in AddressingMode]: () => void } = {
        [AddressingMode.Immediate]: () => {
            const addr = cpu.PC++;
            cpu.addrResult = { address: addr, pageCrossed: false };
            logger.log(3, () => `Immediate: address=0x${padHex(addr, 4)}`);
        },
        [AddressingMode.ZeroPage]: () => {
            const addr = cpu.getMemory().read(cpu.PC++);
            cpu.addrResult = { address: addr, pageCrossed: false };
            logger.log(3, () => `ZeroPage: address=0x${padHex(addr, 2)} from PC=0x${padHex(cpu.PC - 1, 4)}`);
        },
        [AddressingMode.ZeroPageX]: () => {
            const base = cpu.getMemory().read(cpu.PC++);
            const addr = (base + cpu.X) & 0xFF;
            cpu.addrResult = { address: addr, pageCrossed: false };
            logger.log(3, () => `ZeroPageX: base=0x${padHex(base, 2)}, X=0x${padHex(cpu.X, 2)}, address=0x${padHex(addr, 2)}`);
        },
        [AddressingMode.ZeroPageY]: () => {
            const base = cpu.getMemory().read(cpu.PC++);
            const addr = (base + cpu.Y) & 0xFF;
            cpu.addrResult = { address: addr, pageCrossed: false };
            logger.log(3, () => `ZeroPageY: base=0x${padHex(base, 2)}, Y=0x${padHex(cpu.Y, 2)}, address=0x${padHex(addr, 2)}`);
        },
        [AddressingMode.Absolute]: () => {
            const addr = cpu.getMemory().read16(cpu.PC);
            cpu.PC += 2;
            cpu.addrResult = { address: addr, pageCrossed: false };
            logger.log(3, () => `Absolute: address=0x${padHex(addr, 4)} from PC=0x${padHex(cpu.PC - 2, 4)}`);
        },
        [AddressingMode.AbsoluteX]: () => {
            const base = cpu.getMemory().read16(cpu.PC);
            const addr = (base + cpu.X) & 0xFFFF;
            cpu.PC += 2;
            cpu.addrResult = { address: addr, pageCrossed: (base & 0xFF00) !== (addr & 0xFF00) };
            logger.log(3, () => `AbsoluteX: base=0x${padHex(base, 4)}, X=0x${padHex(cpu.X, 2)}, address=0x${padHex(addr, 4)}, pageCrossed=${cpu.addrResult.pageCrossed}`);
        },
        [AddressingMode.AbsoluteY]: () => {
            const base = cpu.getMemory().read16(cpu.PC);
            const addr = (base + cpu.Y) & 0xFFFF;
            cpu.PC += 2;
            cpu.addrResult = { address: addr, pageCrossed: (base & 0xFF00) !== (addr & 0xFF00) };
            logger.log(3, () => `AbsoluteY: base=0x${padHex(base, 4)}, Y=0x${padHex(cpu.Y, 2)}, address=0x${padHex(addr, 4)}, pageCrossed=${cpu.addrResult.pageCrossed}`);
        },
        [AddressingMode.Indirect]: () => {
            const ptr = cpu.getMemory().read16(cpu.PC);
            cpu.PC += 2;
            const lo = cpu.getMemory().read(ptr);
            const hi = cpu.getMemory().read((ptr & 0xFF00) | ((ptr + 1) & 0xFF));
            const addr = (hi << 8) | lo;
            cpu.addrResult = { address: addr, pageCrossed: false };
            logger.log(3, () => `Indirect: ptr=0x${padHex(ptr, 4)}, address=0x${padHex(addr, 4)}`);
        },
        [AddressingMode.IndexedIndirect]: () => {
            const ptr = (cpu.getMemory().read(cpu.PC++) + cpu.X) & 0xFF;
            const lo = cpu.getMemory().read(ptr);
            const hi = cpu.getMemory().read((ptr + 1) & 0xFF);
            const addr = (hi << 8) | lo;
            cpu.addrResult = { address: addr, pageCrossed: false };
            logger.log(3, () => `IndexedIndirect: ptr=0x${padHex(ptr, 2)}, X=0x${padHex(cpu.X, 2)}, address=0x${padHex(addr, 4)}`);
        },
        [AddressingMode.IndirectIndexed]: () => {
            const ptr = cpu.getMemory().read(cpu.PC++);
            const lo = cpu.getMemory().read(ptr);
            const hi = cpu.getMemory().read((ptr + 1) & 0xFF);
            const base = (hi << 8) | lo;
            const addr = (base + cpu.Y) & 0xFFFF;
            cpu.addrResult = { address: addr, pageCrossed: (base & 0xFF00) !== (addr & 0xFF00) };
            logger.log(3, () => `IndirectIndexed: ptr=0x${padHex(ptr, 2)}, base=0x${padHex(base, 4)}, Y=0x${padHex(cpu.Y, 2)}, address=0x${padHex(addr, 4)}, pageCrossed=${cpu.addrResult.pageCrossed}`);
        },
        [AddressingMode.Implied]: () => {
            logger.log(3, () => `Implied: no address calculation`);
        },
    };

    return addressingMap[mode] || (() => {
        logger.error(`Invalid addressing mode: ${mode}`);
    });
}